import {
  GroupLevelAttribute,
  GroupLevelAttributeTemplate,
  GroupLevelAttributeValue,
  GroupLevelAttributeWithSpec
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { SortOrder } from '@/store/types/general.types';

/* NOTE:
 * Whenever there's any update in this file,
 * please remember to update their counterparts in jbi-shared.
 * file path: jbi-shared/types/group-level-attributes.types.ts */

export enum GroupLevelAttributeValueStatus {
  'DELETE' = 'DELETE',
  'ACTIVE' = 'ACTIVE'
}

export enum GroupLevelAttributeTemplateStatus {
  'DELETE' = 'DELETE',
  'ACTIVE' = 'ACTIVE'
}

export enum AddressCountryStatus {
  'DELETE' = 'DELETE',
  'ACTIVE' = 'ACTIVE'
}

export enum AddressStateStatus {
  'DELETE' = 'DELETE',
  'ACTIVE' = 'ACTIVE'
}

export enum GroupTemplateStatus {
  'DELETE' = 'DELETE',
  'ACTIVE' = 'ACTIVE'
}

export enum GroupTemplateAttributeStatus {
  'DELETE' = 'DELETE',
  'ACTIVE' = 'ACTIVE'
}

// Expand list as necessary
export enum ContactTypeFieldIndexes {
  NAME = 0,
  EMAIL = 1,
  PHONE = 2
}

/**
 * This interface covers the overlapped properties for both
 * Group Level Attributes and Group Template Attributes.
 */
export interface GroupAttribute {
  order: number;
  label: string;
  isRequired: boolean;
  groupLevelAttributeType: GroupLevelAttributeType;
  id?: number;
  createdDate?: Date;
  updatedDate?: Date;
  deletedDate?: Date | null;
  hasDuplicationError?: boolean;
}

export type AttributeValueType =
  | GroupLevelAttributeContactsType[]
  | GroupLevelAttributeOption[]
  | GroupLevelAttributeListType
  | string
  | string[]
  | null;

export type AttributeOptionValueType = string | string[] | null;

export interface GroupLevelAttributeListType {
  options: string[];
  selected: string[] | string;
  isSingleSelection: boolean;
}

export interface GroupLevelAttributeContactsType {
  id?: number;
  contactOptions: GroupLevelAttributeOption[];
  // property is for used for frontend validation purposes
  hasFieldError: boolean;
  errorMessage?: ContactOptionsErrorMessages;
}

export interface ContactOptionsErrorMessages {
  nameErrorMessage: string;
  emailErrorMessage: string;
  phoneErrorMessage: string;
}

export interface GroupLevelAttributeOptionType {
  id: number;
  type: string;
}

export interface GroupLevelAttributeOption {
  placeholder: string;
  value: AttributeOptionValueType;
  status: GroupLevelAttributeValueStatus;
  attributeType: GroupLevelAttributeOptionType;
  isRequired?: boolean;
}

export interface GroupLevelAttributeType {
  id: number;
  type: string;
  option:
    | GroupLevelAttributeOption[]
    | GroupLevelAttributeListType
    | GroupLevelAttributeContactsType[]
    | null;
}

// TODO: Remove after refactor
export interface GroupLevelAttributeValueOld extends GroupAttribute {
  value: AttributeValueType;
  status: GroupLevelAttributeValueStatus;
  // props below are only being used in front end for rendering
  hasFieldError?: boolean;
  isTitle?: boolean;
  isDefault?: boolean;
  isDisabled?: boolean;
  isDraggable?: boolean;
}

// TODO: Remove after refactor
export interface DraggedAttributeValueEvent {
  moved: {
    element: GroupLevelAttributeValueOld;
    oldIndex: number;
    newIndex: number;
  };
}

// TODO: Remove after refactor
export interface GroupLevelAttributeValuePayload {
  // Optional groupId for modified create GLA flow in courses
  // FIXME: GLAs should be created within the same API call as group creation
  //        Currently persisted to avoid breaking existing implementations
  groupId?: number;
  attributeValues: {
    selected: GroupLevelAttributeValueOld[];
    deleted?: GroupLevelAttributeValueOld[];
  };
}

export interface GroupLevelAttributeValueErrorMsg {
  label: string;
  field: string;
}

export interface GroupLevelAttributeUploadableSignedUrlRequestPayload {
  fileName: string;
  contentType: string;
  isLogo?: boolean;
}

export interface GroupTitleAndLogo {
  title: string;
  logoUrl: string;
}

export interface UploadableFieldMessage {
  message: string;
  type: string;
}

export const DocumentFileSizeLimit = 5000000;
export const ImageFileSizeLimit = 2000000;

export interface GetSignedUrlForUploadResponsePayload {
  signedUrl: string;
  storageUrl: string;
  storageUri: string;
  fileName: string;
}

export interface GroupTemplateAttribute extends GroupAttribute {
  option: AttributeValueType;
  status: GroupTemplateAttributeStatus;
}

export interface GroupTemplatePayload {
  id?: number;
  templateName: string;
  attributes: {
    active: GroupTemplateAttribute[];
    deleted?: GroupTemplateAttribute[];
  };
  groupId?: number;
}

export interface GroupTemplateSortSpecs {
  sortColumn: string;
  sortOrder: SortOrder;
}

// TODO: Remove after refactor
export interface GroupTemplate {
  id?: number;
  name: string;
  status?: GroupTemplateStatus;
  groupTemplateAttributes: GroupTemplateAttribute[];
}

/* Total number of template attributes to display before "view more" */
export const TemplateAttributeDisplayCount: number = 5;

/* Filtered payload used when fetching group templates */
export interface FilteredGroupTemplatesPayload {
  templateName?: string;
  sortOrder?: SortOrder;
  sortColumn?: string;
  page?: number;
  limit?: number;
}

export interface AddressFilterOption {
  countryName?: string;
  stateName?: string;
}

export interface AddressCountry {
  id: number;
  name: string;
  country_code: string;
  country_code_alpha3: string;
  phone: string;
  currency: string;
  status: AddressCountryStatus;
  states: AddressState[];
}

export interface CurrencyOptions {
  id: number;
  countryName: string;
  currency: string;
}

export interface AddressState {
  id: number;
  name: string;
}

export interface GlobalResponse {
  message: string;
}

export interface CreateGroupLevelAttributeResponse extends GlobalResponse {
  createdGroupLevelAttribute: GroupLevelAttribute;
}

export interface UpdateGroupLevelAttributeResponse extends GlobalResponse {
  updatedGroupLevelAttribute: GroupLevelAttribute;
}

export interface CreateGroupLevelAttributeTemplateResponse
  extends GlobalResponse {
  createdGroupLevelAttributeTemplate: GroupLevelAttributeTemplate;
}

export interface UpdateGroupLevelAttributeTemplateResponse
  extends GlobalResponse {
  updatedGroupLevelAttributeTemplate: GroupLevelAttributeTemplate;
}

/* Filtered payload used when fetching group level attributes */
export interface FilteredGroupLevelAttributesPayload {
  attributeLabel?: string;
  sortOrder?: SortOrder;
  sortColumn?: string;
  page?: number;
  limit?: number;
}

export interface ActiveDeletedAttributes {
  active: GroupLevelAttributeWithSpec[];
  deleted: GroupLevelAttributeWithSpec[];
}

export interface InitialActiveDeletedAttributes
  extends ActiveDeletedAttributes {
  initial: GroupLevelAttributeValue[];
}

export enum LocationType {
  REMOTE = 'Remote',
  VENUE = 'Venue',
  BOTH = 'Both'
}

export interface VenueFieldData {
  building: string;
  lineOne: string;
  lineTwo: string;
  city: string;
  postcode: string;
  country: string;
  state: string;
}

export enum AddressVenueFieldOptionIndexes {
  BUILDING = 0,
  LINEONE = 1,
  LINETWO = 2,
  CITY = 3,
  POSTCODE = 4,
  STATE = 5,
  COUNTRY = 6
}
